<template>
  <!-- Show nothing until auth resolves — prevents flash before Zitadel redirect -->
  <div v-if="!authDisabled && !isAuthenticated" />
  <div v-else id="map-kit-app" :class="{ 'screenshot-active': screenshotMode }">
    <header v-if="!screenshotMode" class="map-kit-topbar">
      <div class="map-kit-topbar-brand">
        <img class="map-kit-topbar-logo" :src="logoUrl" alt="OroraTech" />
        <span class="map-kit-topbar-title">
          <span class="map-kit-topbar-labs">LABS</span>
          <strong>OroraTech </strong>
          <span>Overpass Planner</span>
        </span>
      </div>

      <div class="map-kit-topbar-center">
        <MapViewToggle variant="topbar" />
      </div>

      <div class="map-kit-topbar-actions">
        <div class="map-kit-topbar-search">
          <MapGeocoder />
        </div>
        <Button
          v-tooltip.bottom="userEmail ?? (authDisabled ? 'Local Profile Preview' : 'Profile')"
          icon="fas fa-user fa-lg"
          severity="contrast"
          size="small"
          text
          class="map-kit-topbar-profile"
          @click="(e: Event) => profilePopover?.toggle(e)"
        />
      </div>
      <Popover ref="profilePopover">
        <div class="map-kit-profile-popover">
          <div v-if="userEmail" class="map-kit-profile-email">{{ userEmail }}</div>
          <div v-else-if="authDisabled" class="map-kit-profile-email">Local development mode</div>
          <button class="map-kit-profile-item" @click="openUserSettings()">
            <FontAwesomeIcon icon="fas fa-gear" fixed-width />
            <span>User Settings</span>
          </button>
          <button
            class="map-kit-profile-item map-kit-profile-item--danger"
            :disabled="logoutDisabled"
            @click="onLogoutClick"
          >
            <FontAwesomeIcon icon="fas fa-arrow-right-from-bracket" fixed-width />
            <span>Logout</span>
          </button>
        </div>
      </Popover>
    </header>

    <div class="map-kit-center">
      <div class="map-kit-center-main">
        <div class="map-kit-map-wrap">
          <MapContainer
            target-id="demo-map"
            :options="mapOptions"
          >
            <template #controls>
              <MapControls />
            </template>

            <MapCloudLayer />
            <MapSatelliteLayer />
            <MapHexGridLayer />
            <MapAoiLayer />

            <div
              v-if="mapDataLoading && !screenshotMode"
              class="map-kit-map-loading"
              role="status"
              aria-live="polite"
            >
              <FontAwesomeIcon icon="fas fa-circle-notch" spin />
              <span>{{ $t('list.loading') }}</span>
            </div>

            <MapTimeControlsOverlay
              v-if="!screenshotMode"
              :panel-visible="showBottomPanel"
              @toggle-panel="showBottomPanel = !showBottomPanel"
            />
            <MapHexLegend />
            <MapScreenshotBranding />

            <div v-if="showZoomHint && !screenshotMode" class="map-kit-zoom-hint">
              <FontAwesomeIcon icon="fas fa-magnifying-glass" />
              <span>{{ $t('common.zoomInHint') }}</span>
            </div>

            <MapPopup v-if="!screenshotMode" :popup="activePopup" @close="activePopup = null" />
          </MapContainer>
        </div>
      </div>

      <MapBottomPanel
        :visible="showBottomPanel"
        :aoi-monitor-auto-open-version="aoiMonitorAutoOpenVersion"
        @close="showBottomPanel = false"
      />

      <button
        v-if="!showBottomPanel && !screenshotMode"
        class="map-kit-bottom-panel-collapsed-bar"
        @click="showBottomPanel = true"
      >
        <FontAwesomeIcon icon="fas fa-chevron-up" />
        <span>{{ $t('timeControls.showPanel') }}</span>
      </button>
    </div>

    <div
      v-if="showUserSettingsModal && !screenshotMode"
      class="map-kit-settings-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="map-kit-user-settings-title"
      @click.self="closeUserSettings"
    >
      <div class="map-kit-settings-modal">
        <div class="map-kit-settings-modal-header">
          <h3 id="map-kit-user-settings-title" class="map-kit-settings-modal-title">User Settings</h3>
          <button
            class="map-kit-settings-modal-close"
            type="button"
            aria-label="Close user settings"
            @click="closeUserSettings"
          >
            <FontAwesomeIcon icon="fas fa-xmark" />
          </button>
        </div>
        <MapSettingsControls />
      </div>
    </div>

    <div v-if="showAoiStartupPrompt" class="map-kit-startup-overlay" role="dialog" aria-modal="true">
      <input
        ref="startupFileInput"
        type="file"
        accept=".json,.geojson"
        multiple
        style="display: none"
        @change="onStartupFileChange"
      />
      <div class="map-kit-startup-card">
        <h3 class="map-kit-startup-title">{{ $t('startup.title') }}</h3>
        <p class="map-kit-startup-body">{{ $t('startup.body') }}</p>

        <div
          :class="['map-kit-startup-dropzone', { 'map-kit-startup-dropzone--dragover': isStartupDragOver }]"
          @click="startupFileInput?.click()"
          @dragover.prevent="isStartupDragOver = true"
          @dragenter.prevent="isStartupDragOver = true"
          @dragleave.prevent="isStartupDragOver = false"
          @drop.prevent="onStartupDrop"
        >
          <div class="map-kit-startup-dropzone-icon">
            <FontAwesomeIcon :icon="isStartupDragOver ? 'fas fa-file-import' : 'fas fa-cloud-arrow-up'" />
          </div>
          <p class="map-kit-startup-dropzone-title">
            {{ isStartupDragOver ? $t('monitor.dropFiles') : $t('startup.dropzoneTitle') }}
          </p>
          <p v-if="!isStartupDragOver" class="map-kit-startup-dropzone-subtitle">
            {{ $t('startup.dropzoneSubtitle') }}
          </p>
          <p v-if="!isStartupDragOver" class="map-kit-startup-dropzone-hint">
            {{ $t('startup.dropzoneHint') }}
          </p>
        </div>

        <div class="map-kit-startup-divider">
          <span>{{ $t('startup.or') }}</span>
        </div>

        <Button
          :label="$t('startup.continueGlobal')"
          icon="fas fa-globe"
          size="small"
          severity="secondary"
          class="startup-btn startup-btn--secondary"
          @click="onStartupContinueGlobal"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import Button from 'primevue/button';
import Popover from 'primevue/popover';

import logoUrl from '@/assets/img/orora_icon_red.svg?url';
import type { PopupContent } from '@/types';
import { useFilterStore } from '@/composables/useFilterStore';
import { useMap } from '@/composables/useMap';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import MapBottomPanel from './components/MapBottomPanel.vue';
import MapContainer from './components/MapContainer.vue';
import MapControls from './components/MapControls.vue';
import MapGeocoder from './components/MapGeocoder.vue';
import MapSettingsControls from './components/MapSettingsControls.vue';
import MapPopup from './components/MapPopup.vue';
import MapCloudLayer from './components/MapCloudLayer.vue';
import MapSatelliteLayer from './components/MapSatelliteLayer.vue';
import MapHexGridLayer from './components/MapHexGridLayer.vue';
import MapViewToggle from './components/MapViewToggle.vue';
import MapHexLegend from './components/MapHexLegend.vue';
import MapAoiLayer from './components/MapAoiLayer.vue';
import MapTimeControlsOverlay from './components/MapTimeControlsOverlay.vue';
import MapScreenshotBranding from './components/MapScreenshotBranding.vue';
import { useAoiLibrary } from '@/composables/useAoiLibrary';
import { useScreenshotMode } from '@/composables/useScreenshotMode';
import { useAoiStore } from '@/composables/useAoiStore';
import { useMonitoredAreasStore } from '@/composables/useMonitoredAreasStore';
import { useAoiWarming, type WarmableArea } from '@/composables/useAoiWarming';
import { useAuth } from '@/composables/useAuth';
import type { LonLat } from '@/types';

const { screenshotMode } = useScreenshotMode();
const { isAuthenticated, userEmail, authDisabled, logout } = useAuth();

const {
  loadMetadata,
  selectedHexCells,
  mapViewMode,
  swathLoading,
} = useFilterStore();
const { entries: aoiLibraryEntries } = useAoiLibrary();
const { warmAreas } = useAoiWarming();
const { loadAoiFile, submitCoverage, status: aoiStatus } = useAoiStore();
const { hasAreas, areas, addAreaFromAoi, setAreaCoverage, isHydrated: areMonitoredAreasHydrated } = useMonitoredAreasStore();

const profilePopover = ref<InstanceType<typeof Popover> | null>(null);
const showUserSettingsModal = ref(false);
const showBottomPanel = ref(true);
const aoiMonitorAutoOpenVersion = ref(0);
const logoutDisabled = computed(() => authDisabled);
const mapDataLoading = computed(() => (
  (mapViewMode.value === 'swath' && swathLoading.value)
  || (mapViewMode.value === 'aoi' && aoiStatus.value === 'uploading')
));

const showAoiStartupPrompt = ref(false);
const startupPromptEvaluated = ref(false);
const isStartupDragOver = ref(false);
const startupFileInput = ref<HTMLInputElement | null>(null);
const defaultCenter: LonLat = [11.576, 48.137];
const mapCenter = ref<LonLat>([...defaultCenter] as LonLat);
const MAP_VIEW_MODE_STORAGE_KEY = 'overpassPlanner.mapViewMode';

const mapOptions = computed(() => ({
  center: mapCenter.value,
  zoom: 6.75,
  minZoom: 4.5,
  maxZoom: 10,
}));

function openUserSettings(): void {
  profilePopover.value?.hide();
  showUserSettingsModal.value = true;
}

function closeUserSettings(): void {
  showUserSettingsModal.value = false;
}

function onLogoutClick(): void {
  if (logoutDisabled.value) {
    profilePopover.value?.hide();
    return;
  }
  void logout();
}

const activePopup = ref<PopupContent | null>(null);

const { map, setCenter } = useMap();
const showZoomHint = ref(false);

function openBottomPanelUnlessStartupPrompt(): void {
  if (showAoiStartupPrompt.value) return;
  showBottomPanel.value = true;
}

watch(
  () => [...selectedHexCells.value],
  (cells, prev) => {
    if (cells.length > 0 && JSON.stringify(cells) !== JSON.stringify(prev ?? [])) {
      openBottomPanelUnlessStartupPrompt();
    }
  },
);

watch(
  () => areas.value.some((area) => area.coverage_geojson != null),
  (hasComputedCoverage) => {
    if (hasComputedCoverage) {
      openBottomPanelUnlessStartupPrompt();
    }
  },
  { immediate: true },
);

watch(mapViewMode, (mode, prev) => {
  if (mode === 'aoi' && prev !== 'aoi') {
    openBottomPanelUnlessStartupPrompt();
  }
});

function getApproxUserLocation(): Promise<LonLat | null> {
  if (typeof window === 'undefined' || !navigator.geolocation) return Promise.resolve(null);
  return new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve([pos.coords.longitude, pos.coords.latitude]),
      () => resolve(null),
      {
        enableHighAccuracy: false,
        timeout: 3500,
        maximumAge: 6 * 60 * 60 * 1000,
      },
    );
  });
}

function shouldShowAoiStartupPrompt(): boolean {
  const noAoiConfigured = aoiLibraryEntries.value.length === 0 && !hasAreas.value;
  if (!noAoiConfigured) return false;
  if (typeof window === 'undefined') return true;
  try {
    const persistedView = window.localStorage.getItem(MAP_VIEW_MODE_STORAGE_KEY);
    if (!persistedView) return true;
    return persistedView === 'aoi';
  } catch {
    return true;
  }
}

function evaluateStartupPromptOnce(): void {
  if (startupPromptEvaluated.value) return;
  if (!areMonitoredAreasHydrated.value) return;
  showAoiStartupPrompt.value = shouldShowAoiStartupPrompt();
  if (showAoiStartupPrompt.value) {
    showBottomPanel.value = false;
  }
  startupPromptEvaluated.value = true;
}

onMounted(async () => {
  await useAuth().init();
  if (!authDisabled && !isAuthenticated.value) return;
  loadMetadata();
  evaluateStartupPromptOnce();
  void (async () => {
    const loc = await getApproxUserLocation();
    if (!loc) return;
    mapCenter.value = loc;
    if (map.value) setCenter(loc, 0);
  })();
});

watch(areMonitoredAreasHydrated, (hydrated) => {
  if (!hydrated) return;
  evaluateStartupPromptOnce();
  warmAvailableAreas();
}, { immediate: true });

/**
 * Prefetch coverage for every area this user can open, in the background.
 *
 * Deliberately not awaited: the map must not wait on it, and a failed warm is a
 * non-event (the area still opens, just not instantly).  Geometry is excluded —
 * that is what makes warming every area affordable — so opening an area still
 * fetches its polygons, but the times, coverage and cloud are already there.
 */
function warmAvailableAreas(): void {
  const seen = new Set<string>();
  const warmable: WarmableArea[] = [];
  for (const area of areas.value) {
    const geojson = area.aoi_geojson as Record<string, unknown> | undefined;
    if (!geojson || seen.has(area.name)) continue;
    seen.add(area.name);
    warmable.push({ name: area.name, geojson });
  }
  for (const entry of aoiLibraryEntries.value) {
    if (seen.has(entry.name)) continue;
    seen.add(entry.name);
    warmable.push({ name: entry.name, geojson: entry.geojson });
  }
  if (warmable.length) void warmAreas(warmable);
}

function dismissStartupPrompt(): void {
  showAoiStartupPrompt.value = false;
}

async function processStartupFiles(files: FileList | File[]) {
  for (const file of Array.from(files)) {
    if (!file.name.endsWith('.json') && !file.name.endsWith('.geojson')) continue;
    const loaded = await loadAoiFile(file);
    if (!loaded) continue;
    const rawName = loaded.file.name.replace(/\.(geo)?json$/i, '') || 'Uploaded AOI';
    const area = addAreaFromAoi(rawName, loaded.geojson);
    const result = await submitCoverage();
    if (result) {
      setAreaCoverage(area.id, result.geojson, result.summary, result.queriedAt);
    }
  }
  showBottomPanel.value = true;
  dismissStartupPrompt();
}

async function onStartupFileChange(evt: Event) {
  const input = evt.target as HTMLInputElement;
  const files = input.files;
  if (!files?.length) return;
  await processStartupFiles(files);
  input.value = '';
}

async function onStartupDrop(evt: DragEvent) {
  isStartupDragOver.value = false;
  const files = evt.dataTransfer?.files;
  if (!files?.length) return;
  await processStartupFiles(files);
}

function onStartupContinueGlobal(): void {
  aoiMonitorAutoOpenVersion.value += 1;
  mapViewMode.value = 'grid';
  showBottomPanel.value = true;
  dismissStartupPrompt();
}
</script>

<style lang="scss">
@use '@/style/variables' as vars;

.map-kit-profile-popover {
  display: flex;
  flex-direction: column;
  min-width: 12rem;
}

.map-kit-profile-email {
  padding: 0.5rem 0.75rem;
  font-size: 0.8rem;
  opacity: 0.65;
  border-bottom: 1px solid var(--p-content-border-color, #e2e8f0);
  word-break: break-all;
}

.map-kit-profile-item {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.55rem 0.75rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--p-text-color, #1e293b);
  text-align: left;
  width: 100%;

  &:hover {
    background: var(--p-content-hover-background, #f1f5f9);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
    background: transparent;
  }

  &--danger {
    color: var(--p-red-500, #ef4444);
  }
}

.map-kit-settings-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 30;
}

.map-kit-settings-modal {
  width: min(420px, calc(100% - 2rem));
  max-height: min(70vh, 640px);
  overflow: auto;
  background: var(--p-content-background, #fff);
  border: 1px solid var(--p-content-border-color, #d1d5db);
  border-radius: 14px;
  box-shadow: 0 18px 48px rgba(15, 23, 42, 0.28);
  padding: 1rem;
}

.map-kit-settings-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.map-kit-settings-modal-title {
  margin: 0;
  font-size: 1.05rem;
}

.map-kit-settings-modal-close {
  width: 2rem;
  height: 2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--p-content-border-color, #d1d5db);
  border-radius: 999px;
  background: transparent;
  color: var(--p-text-color, #1e293b);
  cursor: pointer;

  &:hover {
    background: var(--p-content-hover-background, #f1f5f9);
  }
}

#map-kit-app {
  display: grid;
  grid-template-rows: vars.$topbar-height 1fr;
  grid-template-columns: 1fr;
  height: 100%;
  background: var(--p-content-background, #fff);
  color: var(--p-text-color, #1e293b);

  &.screenshot-active {
    grid-template-rows: 1fr;
  }
}

#map-kit-app .map-kit-topbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 1rem;
  padding: 0 0.75rem 0 0.5rem;
}

#map-kit-app .map-kit-topbar-brand {
  grid-column: 1;
  display: inline-flex;
  align-items: center;
  justify-self: start;
  min-width: 0;
  overflow: hidden;
}

#map-kit-app .map-kit-topbar-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

#map-kit-app .map-kit-topbar-center {
  grid-column: 2;
  justify-self: center;
  width: auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

#map-kit-app .map-kit-topbar-actions {
  grid-column: 3;
  justify-self: end;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.85rem;
  min-width: 0;
  max-width: min(46rem, calc(100vw - 18rem));
}

#map-kit-app .map-kit-topbar-search {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .map-kit-geocoder {
    margin: 0;
    justify-content: flex-end;
    max-width: min(32rem, 100%);
    width: auto;
    gap: 0.35rem;
  }

  .map-kit-autocomplete {
    width: clamp(16rem, 24vw, 22rem);
    max-width: 100%;
    flex: 0 1 auto;
  }
}

#map-kit-app .map-kit-topbar-profile {
  width: 2rem;
  height: 2rem;
  color: var(--p-navbar-color, #ffffffcc) !important;
}

#map-kit-app .map-kit-topbar-labs {
  display: inline-flex;
  align-items: center;
  margin: 0 0.4rem 0 0.35rem;
  padding: 0.06rem 0.42rem;
  border-radius: 999px;
  background: #55DF63;
  color: #0f172a;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  line-height: 1.5;
  vertical-align: middle;
}

.map-kit-center {
  grid-column: 1;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;

  .screenshot-active & {
    grid-row: 1;
    grid-column: 1;
  }
}

.map-kit-center-main {
  flex: 1;
  display: flex;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.map-kit-map-wrap {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
}

.map-kit-map-wrap > .map-kit-container {
  flex: 1;
  min-height: 0;
}

.map-kit-bottom-panel-collapsed-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  width: 100%;
  padding: 0.3rem 0;
  border: none;
  border-top: 1px solid rgba(250, 249, 247, 0.08);
  background: var(--p-navbar-background, #0C232B);
  color: var(--p-text-muted-color, #8DA0A7);
  font-family: inherit;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;

  &:hover {
    background: rgba(3, 222, 204, 0.06);
    color: #03decc;
  }

  svg {
    font-size: 0.6rem;
  }
}

:root:not(.color-scheme-dark) .map-kit-bottom-panel-collapsed-bar {
  border-top-color: rgba(15, 23, 42, 0.12);
  color: #64748b;

  &:hover {
    background: rgba(0, 191, 165, 0.06);
    color: #0f766e;
  }
}

.map-kit-startup-overlay {
  position: absolute;
  inset: vars.$topbar-height 0 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20;
}

.map-kit-startup-card {
  width: min(420px, calc(100% - 2rem));
  height: fit-content;
  background: var(--p-content-background, #fff);
  border: 1px solid var(--p-content-border-color, #d1d5db);
  border-radius: 14px;
  padding: 1.75rem 1.75rem 1.5rem;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.22);
  display: flex;
  flex-direction: column;
  align-items: stretch;
}

.map-kit-startup-title {
  margin: 0;
  font-size: 1.35rem;
  text-align: center;
}

.map-kit-startup-body {
  margin: 0.4rem 0 1.1rem;
  color: var(--p-text-muted-color, #64748b);
  font-size: 0.92rem;
  text-align: center;
}

.map-kit-startup-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 2rem 1.5rem;
  border: 2px dashed var(--p-content-border-color, #d1d5db);
  border-radius: 12px;
  background: var(--p-content-background, #fff);
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
  text-align: center;
  user-select: none;

  &:hover {
    border-color: var(--p-primary-color, #00bfa5);
    background: rgba(0, 191, 165, 0.04);
  }

  &--dragover {
    border-color: var(--p-primary-color, #00bfa5);
    background: rgba(0, 191, 165, 0.08);
    box-shadow: 0 0 0 4px rgba(0, 191, 165, 0.12);
  }
}

.map-kit-startup-dropzone-icon {
  font-size: 2.4rem;
  color: var(--p-primary-color, #00bfa5);
  opacity: 0.8;
  margin-bottom: 0.25rem;
}

.map-kit-startup-dropzone-title {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-text-color, #1e293b);
}

.map-kit-startup-dropzone-subtitle {
  margin: 0;
  font-size: 0.88rem;
  color: var(--p-text-muted-color, #64748b);
}

.map-kit-startup-dropzone-hint {
  margin: 0.3rem 0 0;
  font-size: 0.76rem;
  color: var(--p-text-muted-color, #94a3b8);
}

.map-kit-startup-divider {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1rem 0;
  color: var(--p-text-muted-color, #94a3b8);
  font-size: 0.82rem;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--p-content-border-color, #d1d5db);
  }
}

.startup-btn--secondary.p-button {
  width: 100%;
  justify-content: center;
  padding: 0.6rem 1rem;
  font-weight: 600;
  font-size: 0.88rem;
  border-radius: 8px;
  background: #D6DCDE;
  color: #1e293b;
  border: 1px solid #B1BBC0;
  transition: background 0.15s, box-shadow 0.15s;

  &:hover {
    background: #B1BBC0;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
}

@media (max-width: 1080px) {
  #map-kit-app .map-kit-topbar-actions {
    max-width: min(38rem, calc(100vw - 17rem));
  }
}

@media (max-width: 860px) {
  #map-kit-app .map-kit-topbar {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      'brand actions'
      'center center';
    gap: 0.45rem 0.75rem;
    height: auto;
    min-height: vars.$topbar-height;
    padding: 0.45rem 0.75rem 0.55rem 0.5rem;
  }

  #map-kit-app .map-kit-topbar-brand {
    grid-area: brand;
  }

  #map-kit-app .map-kit-topbar-center {
    grid-area: center;
    justify-self: center;
  }

  #map-kit-app .map-kit-topbar-actions {
    grid-area: actions;
    justify-self: end;
    gap: 0.6rem;

    .map-kit-autocomplete {
      width: clamp(13rem, 36vw, 18rem);
    }
  }

  #map-kit-app .map-kit-topbar-search .map-kit-geocoder {
    max-width: min(100%, 20rem);
  }

  .map-kit-startup-overlay {
    inset: 0;
  }
}

@media (max-width: 575px) {
  #map-kit-app .map-kit-topbar {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'brand'
      'center'
      'actions';
    padding-right: 0.5rem;
  }

  #map-kit-app .map-kit-topbar-brand,
  #map-kit-app .map-kit-topbar-actions,
  #map-kit-app .map-kit-topbar-center {
    grid-column: auto;
  }

  #map-kit-app .map-kit-topbar-title span:last-child {
    display: none;
  }

  #map-kit-app .map-kit-topbar-actions {
    justify-self: stretch;
    justify-content: flex-end;
    flex-wrap: wrap;
    row-gap: 0.45rem;
  }

  #map-kit-app .map-kit-topbar-search {
    flex: 1 1 100%;
    justify-content: stretch;

    .map-kit-geocoder {
      max-width: 100%;
      width: 100%;
      justify-content: stretch;
    }

    .map-kit-autocomplete {
      width: 100%;
    }
  }
}
</style>
